import classNames from "classnames";
import { Entry } from "contentful";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";

import Button from "~/components/common/button";
import { LoginSignUpOrigins } from "~/shared/user/login-sign-up-origins.enum";
import Editorials from "~/types/editorials";
import appRoutes from "~/utils/app-routes";
import contentfulUtils from "~/utils/contentful-utils";
import { isActiveEntry } from "~/utils/editorial-utils";
import textUtils from "~/utils/text-utils";

import EscapeNewLine from "../common/escape-new-line";
import HighlightedText from "../common/highlighted-text";
import Icon from "../common/icon";
import styles from "./create-account.module.scss";

type Props = {
  benefits: Entry<Editorials.AccountBenefitItem>[];
  loyaltyProgramLink?: Entry<Editorials.Link>;
};

export default function CreateAccount({ benefits, loyaltyProgramLink }: Props) {
  const t = useTranslations();
  const router = useRouter();

  const goToSignUp = () => {
    const signUpRedirect = new URL([`/${router.locale}`, appRoutes.SIGN_UP].join(""), location.origin);
    router.query.origin && signUpRedirect.searchParams.set("origin", router.query.origin as string);
    router.push(signUpRedirect.href);
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.headline}>{t("generic.create_account_enjoy_exclusive_rewards")}</h2>
      <div className={styles.description}>
        <p className={styles.paragraph}>
          {t.rich("generic.register_now_take_part_KIKO_loyalty_program_with_gradient", {
            gradient: (chunks) => {
              return <span className={styles.gradient}>{chunks?.toString()}</span>;
            },
          })}
        </p>
        {loyaltyProgramLink && isActiveEntry(loyaltyProgramLink) ? (
          <Link
            prefetch={false}
            className={styles.link}
            href={textUtils.sanitizeContentfulUrl(loyaltyProgramLink, router)}
            target={loyaltyProgramLink.fields.openOnANewTab ? "_blank" : undefined}
          >
            {loyaltyProgramLink.fields.text}
          </Link>
        ) : null}
      </div>
      <Button
        onClick={() => goToSignUp()}
        variant="primary"
        className={classNames(router.query.origin === LoginSignUpOrigins.cart && styles.mediaDesktop)}
      >
        {t("generic.create_account")}
      </Button>
      <div className={styles.benefitsContainer}>
        {benefits?.filter(isActiveEntry).map((benefit) => {
          return (
            <div key={benefit.fields.title} className={styles.benefitWrapper}>
              {isActiveEntry(benefit.fields.svgIcon) ? (
                <Icon
                  svgMedia={benefit.fields.svgIcon.fields.svgMedia}
                  name={benefit.fields.svgIcon.fields.svgIconId}
                  width={24}
                  height={24}
                />
              ) : null}
              <div className={styles.descriptionBenefitWrapper}>
                <span
                  className={
                    contentfulUtils.isHighlightText(benefit.fields.title)
                      ? styles.benefitTitleHighlighted
                      : styles.benefitTitle
                  }
                >
                  <HighlightedText text={benefit.fields.title} />
                </span>
                <span className={styles.benefitLabel}>
                  <EscapeNewLine text={benefit.fields.description} />
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {router.query.origin === LoginSignUpOrigins.cart && (
        <Button onClick={() => goToSignUp()} variant="primary" className={styles.mobileOnly}>
          {t("generic.create_account")}
        </Button>
      )}
    </div>
  );
}
